<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { useMessageContext } from '../provider.js';
import BaseBubble from './Base.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import Icon from 'next/icon/Icon.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

const { attachments, sender, conversationId } = useMessageContext();
const getters = useStoreGetters();
const { t } = useI18n();

const isProcessingOCR = ref(false);
const showTranscriptBox = ref(true);
const isTranscriptExpanded = ref(false);
const isCopied = ref(false);
const localTranscribedText = ref('');
const TRANSCRIPT_PREVIEW_LENGTH = 350;

const attachment = computed(() => attachments.value[0]);
const url = computed(() => attachment.value?.dataUrl || '');

const activeTranscriptText = computed(() => {
  return localTranscribedText.value || attachment.value?.transcribedText || '';
});

const hasTranscript = computed(() => Boolean(activeTranscriptText.value && activeTranscriptText.value.trim()));

const isTranscriptLong = computed(
  () => activeTranscriptText.value.length > TRANSCRIPT_PREVIEW_LENGTH
);

const displayedTranscript = computed(() => {
  const text = activeTranscriptText.value;
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const formattedTranscript = computed(() => {
  if (!displayedTranscript.value) return '';
  return new MessageFormatter(displayedTranscript.value).formattedMessage;
});

const fileName = computed(() => {
  if (url.value) {
    const filename = url.value.substring(url.value.lastIndexOf('/') + 1);
    return filename || t('CONVERSATION.UNKNOWN_FILE_TYPE');
  }
  return t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileType = computed(() => {
  return fileName.value.split('.').pop();
});

const senderName = computed(() => {
  return sender?.value?.name || '';
});

const toggleOrTranscribe = async () => {
  if (hasTranscript.value) {
    showTranscriptBox.value = !showTranscriptBox.value;
    return;
  }

  const dataUrl = url.value;
  const accountId = getters.getCurrentAccountId.value;
  const convId = conversationId.value;

  try {
    isProcessingOCR.value = true;
    useAlert("⚙️ Solicitando análise IA (GPT-4o Vision)...");

    const res = await window.axios.post(`/api/v1/accounts/${accountId}/conversations/${convId}/ocr`, {
      attachment_url: dataUrl,
      attachment_id: attachment.value.id,
    });

    if (res.data && res.data.status === 'success') {
      if (res.data.text) {
        localTranscribedText.value = res.data.text;
      }
      showTranscriptBox.value = true;
      useAlert("✅ Documento analisado com sucesso!");
    } else {
      useAlert("❌ Erro ao analisar documento.");
    }
  } catch (err) {
    useAlert("❌ Falha na conexão com o servidor OCR.");
    console.error("OCR PDF Error:", err);
  } finally {
    isProcessingOCR.value = false;
  }
};

const copyTranscript = async () => {
  if (!activeTranscriptText.value) return;
  try {
    await navigator.clipboard.writeText(activeTranscriptText.value);
    isCopied.value = true;
    setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch (e) {
    useAlert('Erro ao copiar texto.');
  }
};
</script>

<template>
  <BaseBubble class="overflow-hidden p-3 min-w-72" data-bubble-name="attachment">
    <div class="grid gap-3">
      <!-- File Header -->
      <div class="flex items-start gap-3">
        <div class="size-9 rounded-lg grid place-content-center bg-n-alpha-3 shrink-0">
          <FileIcon :file-type="fileType" class="size-4" />
        </div>
        <div class="space-y-0.5 overflow-hidden flex-1 min-w-0">
          <div v-if="senderName" class="text-n-slate-12 text-xs truncate font-medium">
            {{ t('CONVERSATION.SHARED_ATTACHMENT.FILE', { sender: senderName }) }}
          </div>
          <div class="truncate text-sm text-n-slate-11 font-mono">
            {{ decodeURI(fileName) }}
          </div>
        </div>

        <!-- Botão de Análise de PDF com IA -->
        <button
          class="p-0 border-0 size-8 shrink-0 grid place-content-center rounded-lg transition-colors cursor-pointer"
          :class="[
            hasTranscript
              ? 'text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/30'
              : 'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'
          ]"
          :title="
            isProcessingOCR
              ? 'Analisando documento...'
              : hasTranscript
              ? 'Ver / Ocultar transcrição'
              : 'Transcrever documento com IA'
          "
          :disabled="isProcessingOCR"
          @click="toggleOrTranscribe"
        >
          <Icon
            v-if="isProcessingOCR"
            class="size-4 animate-spin text-woot-600 dark:text-woot-400"
            icon="i-lucide-loader-2"
          />
          <Icon
            v-else-if="hasTranscript"
            class="size-4"
            icon="i-lucide-file-text"
          />
          <Icon
            v-else
            class="size-4"
            icon="i-lucide-sparkles"
          />
        </button>
      </div>

      <!-- Action Button -->
      <div>
        <a
          v-if="url"
          :href="url"
          rel="noreferrer noopener nofollow"
          target="_blank"
          class="w-full block bg-n-solid-3 hover:bg-n-solid-4 px-4 py-1.5 rounded-lg text-xs text-center border border-n-container transition-colors"
        >
          {{ $t('CONVERSATION.DOWNLOAD') }}
        </a>
      </div>

      <!-- Texto de Transcrição Integrado Diretamente no Corpo da Mensagem (Sem Caixa Separada) -->
      <div
        v-if="hasTranscript && showTranscriptBox"
        class="mt-1 flex flex-col gap-2 w-full"
      >
        <div class="flex items-center justify-between">
          <span class="text-[12px] font-semibold text-woot-600 dark:text-woot-400 flex items-center gap-1.5">
            <Icon icon="i-lucide-sparkles" class="size-3.5" />
            <span>Transcrição do Documento</span>
            <span class="opacity-60 text-[10px] font-normal text-n-slate-11">· Generated by Zéti Desk</span>
          </span>
          <button
            class="text-[11px] text-n-slate-11 hover:text-n-slate-12 flex items-center gap-1 bg-n-alpha-2 hover:bg-n-alpha-3 px-2 py-0.5 rounded-md transition-colors cursor-pointer border-0"
            :title="isCopied ? 'Copiado!' : 'Copiar transcrição'"
            @click="copyTranscript"
          >
            <Icon :icon="isCopied ? 'i-lucide-check' : 'i-lucide-copy'" class="size-3 text-woot-500" />
            <span>{{ isCopied ? 'Copiado!' : 'Copiar' }}</span>
          </button>
        </div>

        <div class="text-xs leading-relaxed text-n-slate-12 font-normal">
          <div v-dompurify-html="formattedTranscript" class="prose prose-bubble text-xs [&>p]:mb-1.5 [&>ul]:pl-3 [&>h2]:text-xs [&>h2]:font-bold [&>h3]:text-xs [&>h3]:font-bold" />
        </div>

        <button
          v-if="isTranscriptLong"
          class="self-start p-0 border-0 bg-transparent text-[11px] text-woot-600 dark:text-woot-400 hover:underline font-medium cursor-pointer"
          @click="isTranscriptExpanded = !isTranscriptExpanded"
        >
          {{ isTranscriptExpanded ? 'Mostrar menos' : 'Mostrar mais' }}
        </button>
      </div>
    </div>
  </BaseBubble>
</template>
